import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { osService } from "src/app/_services/osService.service";
import { MapKitService } from "src/app/_services/mapkit.service";
import { Position, SearchPosition } from "src/app/helpers/types";

declare var mapkit: any;

@Component({
  selector: "app-mapkit",
  templateUrl: "./mapkit.component.html",
  styleUrls: ["./mapkit.component.scss"],
  standalone: false,
})
export class MapkitComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() currentLocation?: { latitude: number; longitude: number };
  @Output() dragStart: EventEmitter<void> = new EventEmitter<void>();
  @Output() dragEnd: EventEmitter<void> = new EventEmitter<void>();
  @Output() draging: EventEmitter<void> = new EventEmitter<void>();
  @Output() openSaveModal: EventEmitter<boolean> = new EventEmitter<boolean>();

  map: any = null;
  headerText: string = this.translate.instant("PROVIDER.MAP.DEPLACER_ICON");
  mapTouched: boolean = false;
  searchPosition?: SearchPosition | null | undefined = null;
  isInitialized: boolean = false;
  startPosition: { latitude: number; longitude: number } = {
    latitude: 35.83766268383463,
    longitude: 10.622713348112542,
  };

  markersList: Position[] = [];
  isDragging = false;

  constructor(
    private osService: osService,
    private translate: TranslateService,
    private el: ElementRef,
    private mapKitService: MapKitService
  ) {}

  ngAfterViewInit(): void {
    this.initializeMap();
  }

  ngOnInit() {
    this.getSearchLocation();
    this.startPosition = this.currentLocation
      ? {
          latitude: this.currentLocation.latitude,
          longitude: this.currentLocation.longitude,
        }
      : {
          latitude: 35.83766268383463,
          longitude: 10.622713348112542,
        };
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.mapKitService.destroyMap(this.map);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const locationChange = changes["currentLocation"];
    if (
      locationChange &&
      locationChange.currentValue &&
      locationChange.currentValue !== locationChange.previousValue
    ) {
      const { latitude, longitude } = locationChange.currentValue;
      this.startPosition = { latitude, longitude };
      this.centerMapOnCurrentLocation(latitude, longitude);
    }
  }

  async initializeMap() {
    try {
      await this.mapKitService.initialize();
      const mapElement = document.getElementById("mapkitMapId");
      if (!mapElement) {
        console.error("MapKit container not found");
        return;
      }

      const mapOptions = {
        center: {
          lat: this.startPosition.latitude,
          lng: this.startPosition.longitude,
        },
        zoom: 15,
        // La sélection de position se fait en centrant la carte : elle doit
        // rester orientée au nord. On désactive la rotation (et la boussole,
        // inutile sans rotation) pour éviter les rotations involontaires sur iOS.
        showsCompass: false,
        showsZoomControl: true,
        isRotationEnabled: false,
      };

      this.map = await this.mapKitService.initializeMap(mapElement, mapOptions);

      if (this.map) {
        this.setupMapEvents();
        this.createMarkers();
      }
    } catch (error) {
      console.error("Error initializing MapKit:", error);
    }
  }

  private centerMapOnCurrentLocation(lat: number, lng: number): void {
    if (!lat || !lng) {
      return;
    }
    if (this.map) {
      this.mapKitService.setMapCenter(this.map, { lat, lng });
      // Rafraîchir le stockage du marker pour rester aligné avec le centre actuel
      setTimeout(() => this.updateMarkerPosition(), 0);
    }
  }

  setupMapEvents() {
    if (!this.map) return;

    // Événement de début de glissement
    this.mapKitService.addEventListener(this.map, "region-change-start", () => {
      if (!this.isDragging) {
        this.isDragging = true;
        this.dragStart.emit();
      }
      this.draging.emit();
    });

    // Événement pendant le glissement
    this.mapKitService.addEventListener(this.map, "region-change", () => {
      if (this.isDragging) {
        this.draging.emit();
        this.updateMarkerPosition();
      }
    });

    // Événement de fin de glissement
    this.mapKitService.addEventListener(this.map, "region-change-end", () => {
      if (this.isDragging) {
        this.isDragging = false;
        this.updateMarkerPosition();
        this.dragEnd.emit();
      }
      if(!this.isInitialized) {
        setTimeout(() => {
          this.isInitialized = true;
        }, 1500);
      }
    });
  }

  updateMarkerPosition() {
    if (!this.map) return;

    const center = this.mapKitService.getCenter(this.map);
    if (center) {
      const markerPosition: Position = {
        label: "",
        coords: {
          latitude: center.latitude,
          longitude: center.longitude,
        },
      };

      localStorage.setItem(
        "markerPosition",
        JSON.stringify({
          lat: center.latitude,
          lng: center.longitude,
        })
      );

      this.osService.setMarkerLocation(markerPosition);
    }
  }

  createMarkers() {
    if (!this.map) return;
    
    // Implémentation pour créer des marqueurs supplémentaires si nécessaire
    for (const markerData of this.markersList) {
      const coordinate = this.mapKitService.createCoordinate(
        markerData.coords.latitude,
        markerData.coords.longitude
      );
      
      const annotation = new mapkit.MarkerAnnotation(coordinate, {
        title: markerData.label,
        color: '#1f94a4',
      });
      
      this.map.addAnnotation(annotation);
    }
  }

  getSearchLocation() {
    this.osService.SearchLocationSubject$.subscribe((searchPosition) => {
      if (searchPosition) {
        this.searchPosition = searchPosition;
        if (this.map && searchPosition.position && searchPosition.position.length > 0) {
          const position = searchPosition.position[0];
          this.mapKitService.setMapCenter(this.map, {
            lat: position.lat,
            lng: position.long,
          });
        }
      }
    });
  }
}

